// src/pages/Dashboard/Admin/DetailsAdherents/components/AttendancePanel.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaCalendarAlt,
  FaChartLine,
  FaCheckCircle,
  FaChevronLeft,
  FaChevronRight,
  FaTimesCircle,
  FaUserCheck,
  FaUserTimes,
} from "react-icons/fa";

import { formatDate, safeArray } from "../utils/adherentDashboardUtils";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function isPresentValue(value) {
  return (
    value === true ||
    value === 1 ||
    value === "1" ||
    String(value).toLowerCase() === "true" ||
    String(value).toLowerCase() === "present" ||
    String(value).toLowerCase() === "présent"
  );
}

function parseAttendanceDate(value) {
  if (!value) return null;

  const date =
    typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)
      ? new Date(`${value}T12:00:00`)
      : new Date(value);

  if (Number.isNaN(date.getTime())) return null;

  return date;
}

function getMonthKey(value) {
  const date = parseAttendanceDate(value);
  if (!date) return "unknown";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");

  return `${year}-${month}`;
}

function formatMonthLabel(monthKey, options = {}) {
  if (!monthKey || monthKey === "unknown") return "Date inconnue";

  const [year, month] = monthKey.split("-");
  const date = new Date(Number(year), Number(month) - 1, 1);

  return date.toLocaleDateString("fr-FR", {
    month: options.short ? "short" : "long",
    year: "numeric",
  });
}

function getGroupLabel(row) {
  return (
    row?.Group?.nom ||
    row?.group_nom ||
    row?.nom_groupe ||
    row?.cours_nom ||
    "Cours non précisé"
  );
}

function getAttendanceRate(presents, total) {
  if (!total) return 0;
  return Math.round((presents / total) * 100);
}

function getRateStyle(rate) {
  if (rate >= 85) {
    return {
      text: "text-emerald-700 dark:text-emerald-300",
      bg: "bg-emerald-500",
      soft: "bg-emerald-50 ring-emerald-200 dark:bg-emerald-500/10 dark:ring-emerald-500/20",
      label: "Très régulier",
    };
  }

  if (rate >= 65) {
    return {
      text: "text-sky-700 dark:text-sky-300",
      bg: "bg-sky-500",
      soft: "bg-sky-50 ring-sky-200 dark:bg-sky-500/10 dark:ring-sky-500/20",
      label: "Régulier",
    };
  }

  if (rate >= 45) {
    return {
      text: "text-amber-700 dark:text-amber-300",
      bg: "bg-amber-500",
      soft: "bg-amber-50 ring-amber-200 dark:bg-amber-500/10 dark:ring-amber-500/20",
      label: "À surveiller",
    };
  }

  return {
    text: "text-rose-700 dark:text-rose-300",
    bg: "bg-rose-500",
    soft: "bg-rose-50 ring-rose-200 dark:bg-rose-500/10 dark:ring-rose-500/20",
    label: "Faible présence",
  };
}

function SummaryCard({ icon: Icon, label, value, helper, className }) {
  return (
    <div
      className={cn(
        "rounded-2xl border p-4 ring-1 ring-transparent",
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.16em] opacity-70">
            {label}
          </p>

          <p className="mt-1 text-2xl font-bold text-slate-950 dark:text-white">
            {value}
          </p>

          {helper && (
            <p className="mt-1 text-xs font-semibold opacity-75">{helper}</p>
          )}
        </div>

        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white/70 shadow-sm dark:bg-slate-950/60">
          <Icon />
        </div>
      </div>
    </div>
  );
}

function MonthNavButton({ direction, month, onClick }) {
  const isPrevious = direction === "previous";
  const Icon = isPrevious ? FaChevronLeft : FaChevronRight;
  const title = isPrevious ? "Mois précédent" : "Mois suivant";

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!month}
      className={cn(
        "flex min-h-12 items-center gap-2 rounded-2xl border px-3 py-2 text-sm font-bold transition active:scale-[0.98]",
        isPrevious ? "justify-start text-left" : "justify-end text-right",
        month
          ? "border-slate-200 bg-white text-slate-700 hover:-translate-y-0.5 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          : "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-300 dark:border-slate-800 dark:bg-slate-900/60 dark:text-slate-700",
      )}
      title={month ? `${title} : ${month.label}` : title}
    >
      {isPrevious && (
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-500 dark:bg-slate-950 dark:text-slate-300">
          <Icon />
        </span>
      )}

      <span className="min-w-0">
        <span className="block text-[10px] uppercase tracking-[0.14em] text-slate-400">
          {title}
        </span>

        <span className="mt-0.5 block max-w-[110px] truncate text-xs sm:max-w-[180px]">
          {month ? month.label : "—"}
        </span>
      </span>

      {!isPrevious && (
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-500 dark:bg-slate-950 dark:text-slate-300">
          <Icon />
        </span>
      )}
    </button>
  );
}

function MonthCard({ month, active, onClick }) {
  const rateStyle = getRateStyle(month.rate);

  return (
    <button
      type="button"
      data-month-key={month.key}
      onClick={() => onClick(month.key)}
      className={cn(
        "min-w-[210px] rounded-[1.35rem] border p-3 text-left transition active:scale-[0.98]",
        active
          ? "border-slate-950 bg-slate-950 text-white shadow-lg shadow-slate-200/70 dark:border-white dark:bg-white dark:text-slate-950 dark:shadow-black/20"
          : "border-slate-200 bg-white hover:-translate-y-0.5 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-950 dark:hover:bg-slate-900",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p
            className={cn(
              "truncate text-sm font-bold capitalize",
              active
                ? "text-white dark:text-slate-950"
                : "text-slate-950 dark:text-white",
            )}
          >
            {month.label}
          </p>

          <p
            className={cn(
              "mt-1 text-xs font-semibold",
              active
                ? "text-white/65 dark:text-slate-500"
                : "text-slate-500 dark:text-slate-400",
            )}
          >
            {month.total} séance{month.total > 1 ? "s" : ""}
          </p>
        </div>

        <div
          className={cn(
            "rounded-full px-2.5 py-1 text-xs font-bold",
            active
              ? "bg-white/15 text-white dark:bg-slate-950/10 dark:text-slate-700"
              : rateStyle.soft,
            active ? "" : rateStyle.text,
          )}
        >
          {month.rate}%
        </div>
      </div>

      <div
        className={cn(
          "mt-3 h-2 overflow-hidden rounded-full",
          active
            ? "bg-white/15 dark:bg-slate-200"
            : "bg-slate-100 dark:bg-slate-800",
        )}
      >
        <div
          className={cn(
            "h-full rounded-full transition-all",
            active ? "bg-white dark:bg-slate-950" : rateStyle.bg,
          )}
          style={{ width: `${month.rate}%` }}
        />
      </div>

      <div
        className={cn(
          "mt-3 flex items-center justify-between text-xs font-bold",
          active
            ? "text-white/80 dark:text-slate-600"
            : "text-slate-500 dark:text-slate-400",
        )}
      >
        <span className="inline-flex items-center gap-1">
          <FaCheckCircle className="text-emerald-500" />
          {month.presents}
        </span>

        <span className="inline-flex items-center gap-1">
          <FaTimesCircle className="text-rose-500" />
          {month.absents}
        </span>

        <span>{rateStyle.label}</span>
      </div>
    </button>
  );
}

function SessionRow({ row }) {
  const present = isPresentValue(row?.presence);

  return (
    <div className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-3 py-2.5 dark:border-slate-800 dark:bg-slate-950">
      <div className="min-w-0">
        <p className="text-sm font-bold text-slate-950 dark:text-white">
          {formatDate(row?.date_presence, true)}
        </p>

        <p className="mt-0.5 truncate text-xs font-semibold text-slate-500 dark:text-slate-400">
          {getGroupLabel(row)}
        </p>
      </div>

      <span
        className={cn(
          "inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold ring-1",
          present
            ? "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20"
            : "bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/20",
        )}
      >
        {present ? <FaCheckCircle /> : <FaTimesCircle />}
        {present ? "Présent" : "Absent"}
      </span>
    </div>
  );
}

export default function AttendancePanel({ attendances }) {
  const monthsScrollerRef = useRef(null);

  const rows = useMemo(() => {
    return safeArray(attendances)
      .filter((row) => row?.date_presence)
      .sort((a, b) => {
        const dateA = parseAttendanceDate(a?.date_presence)?.getTime() || 0;
        const dateB = parseAttendanceDate(b?.date_presence)?.getTime() || 0;

        return dateA - dateB;
      });
  }, [attendances]);

  const monthlyStats = useMemo(() => {
    const map = new Map();

    rows.forEach((row) => {
      const key = getMonthKey(row?.date_presence);
      const present = isPresentValue(row?.presence);

      if (!map.has(key)) {
        map.set(key, {
          key,
          label: formatMonthLabel(key),
          total: 0,
          presents: 0,
          absents: 0,
          rows: [],
        });
      }

      const month = map.get(key);

      month.total += 1;
      month.rows.push(row);

      if (present) {
        month.presents += 1;
      } else {
        month.absents += 1;
      }
    });

    return Array.from(map.values())
      .map((month) => ({
        ...month,
        rate: getAttendanceRate(month.presents, month.total),
        rows: month.rows.sort((a, b) => {
          const dateA = parseAttendanceDate(a?.date_presence)?.getTime() || 0;
          const dateB = parseAttendanceDate(b?.date_presence)?.getTime() || 0;

          return dateA - dateB;
        }),
      }))
      .sort((a, b) => a.key.localeCompare(b.key));
  }, [rows]);

  const globalStats = useMemo(() => {
    const total = rows.length;
    const presents = rows.filter((row) => isPresentValue(row?.presence)).length;
    const absents = total - presents;
    const rate = getAttendanceRate(presents, total);

    return {
      total,
      presents,
      absents,
      rate,
    };
  }, [rows]);

  const [selectedMonthKey, setSelectedMonthKey] = useState("");

  useEffect(() => {
    if (!monthlyStats.length) {
      setSelectedMonthKey("");
      return;
    }

    setSelectedMonthKey((current) => {
      const exists = monthlyStats.some((month) => month.key === current);

      if (exists) return current;

      return monthlyStats[monthlyStats.length - 1].key;
    });
  }, [monthlyStats]);

  useEffect(() => {
    const scroller = monthsScrollerRef.current;

    if (!selectedMonthKey || !scroller) return;

    const selectedNode = scroller.querySelector(
      `[data-month-key="${selectedMonthKey}"]`,
    );

    if (!selectedNode) return;

    const scrollerWidth = scroller.clientWidth;
    const nodeLeft = selectedNode.offsetLeft;
    const nodeWidth = selectedNode.offsetWidth;

    const nextScrollLeft = nodeLeft - scrollerWidth / 2 + nodeWidth / 2;

    scroller.scrollTo({
      left: Math.max(0, nextScrollLeft),
      behavior: "smooth",
    });
  }, [selectedMonthKey]);

  const selectedMonth = useMemo(() => {
    return (
      monthlyStats.find((month) => month.key === selectedMonthKey) ||
      monthlyStats[monthlyStats.length - 1] ||
      null
    );
  }, [monthlyStats, selectedMonthKey]);

  const selectedMonthIndex = useMemo(() => {
    if (!selectedMonth) return -1;
    return monthlyStats.findIndex((month) => month.key === selectedMonth.key);
  }, [monthlyStats, selectedMonth]);

  const previousMonth =
    selectedMonthIndex > 0 ? monthlyStats[selectedMonthIndex - 1] : null;

  const nextMonth =
    selectedMonthIndex >= 0 && selectedMonthIndex < monthlyStats.length - 1
      ? monthlyStats[selectedMonthIndex + 1]
      : null;

  const goPreviousMonth = () => {
    if (!previousMonth) return;
    setSelectedMonthKey(previousMonth.key);
  };

  const goNextMonth = () => {
    if (!nextMonth) return;
    setSelectedMonthKey(nextMonth.key);
  };

  if (!rows.length) {
    return (
      <div className="rounded-[1.5rem] border border-dashed border-slate-300 bg-slate-50 p-6 text-center dark:border-slate-700 dark:bg-slate-900/60">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-slate-400 shadow-sm dark:bg-slate-950">
          <FaCalendarAlt className="text-xl" />
        </div>

        <p className="mt-3 text-sm font-bold text-slate-800 dark:text-slate-100">
          Aucune présence enregistrée
        </p>

        <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
          Les statistiques mensuelles apparaîtront dès les premières séances
          saisies.
        </p>
      </div>
    );
  }

  const globalRateStyle = getRateStyle(globalStats.rate);
  const selectedRateStyle = selectedMonth
    ? getRateStyle(selectedMonth.rate)
    : globalRateStyle;

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <SummaryCard
          icon={FaCalendarAlt}
          label="Séances"
          value={globalStats.total}
          helper={`${monthlyStats.length} mois suivi${
            monthlyStats.length > 1 ? "s" : ""
          }`}
          className="border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300"
        />

        <SummaryCard
          icon={FaUserCheck}
          label="Présences"
          value={globalStats.presents}
          helper="Séances réalisées"
          className="border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300"
        />

        <SummaryCard
          icon={FaUserTimes}
          label="Absences"
          value={globalStats.absents}
          helper="Séances manquées"
          className="border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300"
        />

        <SummaryCard
          icon={FaChartLine}
          label="Taux présence"
          value={`${globalStats.rate}%`}
          helper={globalRateStyle.label}
          className={cn(
            "border-slate-200 text-slate-700 dark:border-slate-700 dark:text-slate-300",
            globalRateStyle.soft,
          )}
        />
      </div>

      <div className="rounded-[1.5rem] border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-800 dark:bg-slate-950">
        <div className="flex flex-col gap-3">
          <div>
            <p className="text-sm font-bold text-slate-950 dark:text-white">
              Suivi mois par mois
            </p>

            <p className="mt-0.5 text-xs leading-5 text-slate-500 dark:text-slate-400">
              Les mois sont affichés de gauche à droite dans l’ordre
              chronologique. Utilisez les boutons pour passer au mois précédent
              ou suivant.
            </p>
          </div>

          {selectedMonth && (
            <div className="grid grid-cols-[1fr_auto_1fr] items-stretch gap-2">
              <MonthNavButton
                direction="previous"
                month={previousMonth}
                onClick={goPreviousMonth}
              />

              <div className="flex min-h-12 min-w-[104px] flex-col items-center justify-center rounded-2xl bg-slate-950 px-3 py-2 text-center text-white dark:bg-white dark:text-slate-950">
                <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-white/50 dark:text-slate-500">
                  Mois
                </span>

                <span className="mt-0.5 max-w-[120px] truncate text-sm font-bold capitalize">
                  {selectedMonth.label}
                </span>

                <span className="mt-0.5 text-[11px] font-semibold text-white/50 dark:text-slate-500">
                  {selectedMonthIndex + 1}/{monthlyStats.length}
                </span>
              </div>

              <MonthNavButton
                direction="next"
                month={nextMonth}
                onClick={goNextMonth}
              />
            </div>
          )}
        </div>

        <div className="relative max-w-full overflow-hidden">
          <div
            ref={monthsScrollerRef}
            className="mt-4 flex max-w-full gap-3 overflow-x-auto overscroll-x-contain pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {monthlyStats.map((month) => (
              <MonthCard
                key={month.key}
                month={month}
                active={selectedMonth?.key === month.key}
                onClick={setSelectedMonthKey}
              />
            ))}
          </div>
        </div>
      </div>

      {selectedMonth && (
        <div className="rounded-[1.5rem] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-sm font-bold capitalize text-slate-950 dark:text-white">
                {selectedMonth.label}
              </p>

              <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
                {selectedMonth.total} séance
                {selectedMonth.total > 1 ? "s" : ""} · {selectedMonth.presents}{" "}
                présence
                {selectedMonth.presents > 1 ? "s" : ""} ·{" "}
                {selectedMonth.absents} absence
                {selectedMonth.absents > 1 ? "s" : ""}
              </p>
            </div>

            <div
              className={cn(
                "rounded-2xl px-4 py-2 text-center ring-1",
                selectedRateStyle.soft,
              )}
            >
              <p className={cn("text-2xl font-bold", selectedRateStyle.text)}>
                {selectedMonth.rate}%
              </p>

              <p
                className={cn("text-[11px] font-bold", selectedRateStyle.text)}
              >
                {selectedRateStyle.label}
              </p>
            </div>
          </div>

          <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
            <div
              className={cn("h-full rounded-full", selectedRateStyle.bg)}
              style={{ width: `${selectedMonth.rate}%` }}
            />
          </div>

          <div className="mt-4 grid max-h-[260px] gap-2 overflow-y-auto pr-1">
            {selectedMonth.rows.map((row, index) => (
              <SessionRow
                key={`${row?.id || row?.date_presence || "attendance"}-${index}`}
                row={row}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
